// src/app/pages/orders/BulkActionPanels.js

import React, { useMemo, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Card, Chip, IconButton, Stack, TextField, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import ConfirmDialog from 'app/shared/widgets/ConfirmDialog';
import orderServices from 'app/services/order-services';
import { useCompanySettings } from 'app/providers/CompanySettingsProvider/CompanySettingsProvider';
import { getDropoffCarrierByKey, getDropoffCarrierDisplay, getDropoffCarrierOptions } from 'app/utils/dropoffCarriers';
import AddressAutocomplete from 'app/widgets/Shared/AddressAutocomplete';
import { MANUAL_ORDER_STATES } from './ManualOrderStateBadge';

/**
 * Paneles de la derecha del boton "Acciones" de Pedidos. La seleccion vive en OrdersList (las
 * casillas estan en las filas) para que no se pierda al cambiar de pagina; aca se muestra, se
 * completa lo que pide la accion y se manda a Orders::BulkAction. Cada pedido va por su
 * cuenta: los que fallan se listan con el motivo y quedan marcados para reintentar.
 *
 * Props comunes:
 *   selected: [{ id, code, state, dropoff_carrier, preparer_name, ... }]
 *   onRemove(id), onClear(), onClose()
 *   onDone({ ok, failed }) despues de aplicar
 */

// No es un carrier del catalogo: el pedido queda sin dropoff ni direccion
// (Distribuidor::EnvioPreparacion::RETIRO_EN_TIENDA).
const RETIRO_EN_TIENDA = { value: 'retiro_tienda', label: 'Retiro en tienda' };

const pedidos = (n) => `${n} ${n === 1 ? 'pedido' : 'pedidos'}`;

// Armazon comun: titulo, pedidos marcados, el formulario de la accion, errores y botones.
const BulkPanelShell = ({ title, icon, selected, onRemove, onClear, onClose, failed, children, footer }) => (
    <Card sx={{ mb: 2, p: 2 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {icon}
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Marca los pedidos con la casilla a la derecha de cada uno. Puedes cambiar de
                    página o filtrar: lo marcado se mantiene.
                </Typography>
            </Box>
            <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
            </IconButton>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
            <Typography variant="body2">Pedidos</Typography>
            <Chip size="small" color="primary" label={`${selected.length} seleccionados`} />
            {selected.length > 0 && <Button size="small" onClick={onClear}>Quitar todos</Button>}
        </Stack>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, maxHeight: 160, overflowY: 'auto' }}>
            {selected.map((o) => (
                <Chip key={o.id} size="small" label={`#${o.code}`} onDelete={() => onRemove(o.id)} />
            ))}
        </Box>

        <Stack spacing={2} sx={{ mt: 2 }}>
            {children}

            {failed?.length > 0 && (
                <Alert severity="error">
                    No se pudo en {failed.length === 1 ? 'un pedido' : `${failed.length} pedidos`}:
                    <Box component="ul" sx={{ m: 0, pl: 2, maxHeight: 160, overflowY: 'auto' }}>
                        {failed.map((f) => <li key={f.id}>#{f.code || f.id}: {f.error}</li>)}
                    </Box>
                </Alert>
            )}

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button size="small" onClick={onClose}>Salir</Button>
                {footer}
            </Stack>
        </Stack>
    </Card>
);

// Manda la accion a Orders::BulkAction y avisa el resultado. `successText(n)` arma el mensaje.
const useBulkApply = ({ action, selected, onDone, successText }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [saving, setSaving] = useState(false);
    const [failed, setFailed] = useState([]);

    const apply = (params = {}) => {
        setSaving(true);
        setFailed([]);
        return orderServices.bulkAction(action, selected.map((o) => o.id), params)
            .then((data) => {
                if (data.ok > 0) enqueueSnackbar(successText(data.ok), { variant: 'success' });
                setFailed(data.failed || []);
                onDone(data);
            })
            .catch((err) => {
                enqueueSnackbar(err?.response?.data?.error || 'No fue posible aplicar la acción', { variant: 'error' });
            })
            .finally(() => setSaving(false));
    };

    return { apply, saving, failed };
};

export const BulkAssignPreparerPanel = ({ preparers, icon, ...props }) => {
    const { selected } = props;
    const [preparer, setPreparer] = useState(null);
    const { apply, saving, failed } = useBulkApply({
        action: 'assign_preparer', selected, onDone: props.onDone,
        successText: (n) => `${preparer?.full_name} asignado a ${pedidos(n)}`,
    });
    const replacing = selected.filter((o) => o.preparer_name && o.preparer_name !== preparer?.full_name);

    return (
        <BulkPanelShell
            {...props}
            title="Asignar preparador"
            icon={icon}
            failed={failed}
            footer={(
                <Button size="small" variant="contained" disabled={saving || !preparer || selected.length === 0}
                    onClick={() => apply({ preparer_id: preparer.id })}>
                    {saving ? 'Asignando…' : `Asignar a ${selected.length}`}
                </Button>
            )}
        >
            <Autocomplete
                size="small"
                options={preparers || []}
                value={preparer}
                onChange={(e, v) => setPreparer(v)}
                getOptionLabel={(p) => p.full_name || ''}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                noOptionsText="No hay preparadores disponibles"
                renderInput={(params) => <TextField {...params} label="Preparador" />}
            />
            {replacing.length > 0 && (
                <Alert severity="warning">
                    {replacing.length === 1 ? 'Un pedido ya tenía' : `${replacing.length} pedidos ya tenían`} otro
                    preparador: quedan con {preparer ? <strong>{preparer.full_name}</strong> : 'el que elijas'}.
                </Alert>
            )}
        </BulkPanelShell>
    );
};

export const BulkChangeStatePanel = ({ icon, ...props }) => {
    const { selected } = props;
    const [state, setState] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const { apply, saving, failed } = useBulkApply({
        action: 'change_state', selected, onDone: props.onDone,
        successText: (n) => `${pedidos(n)} en ${state?.label}`,
    });
    const finishing = state?.value === 'finished';

    return (
        <BulkPanelShell
            {...props}
            title="Cambiar estado"
            icon={icon}
            failed={failed}
            footer={(
                <Button size="small" variant="contained" disabled={saving || !state || selected.length === 0}
                    onClick={() => setConfirmOpen(true)}>
                    {`Cambiar ${selected.length}`}
                </Button>
            )}
        >
            <Autocomplete
                size="small"
                options={MANUAL_ORDER_STATES}
                value={state}
                onChange={(e, v) => setState(v)}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                renderInput={(params) => <TextField {...params} label="Nuevo estado" />}
            />
            {finishing && (
                <Alert severity="info">
                    Finalizar también deja entregadas sus paradas en el viaje, y cierra el viaje si era la última.
                </Alert>
            )}
            <ConfirmDialog
                open={confirmOpen}
                title="Cambiar estado"
                message={<><strong>{pedidos(selected.length)}</strong> quedarán en <strong>{state?.label}</strong>.</>}
                confirmText="Cambiar"
                loading={saving}
                onConfirm={() => apply({ state: state.value }).then(() => setConfirmOpen(false))}
                onClose={() => setConfirmOpen(false)}
            />
        </BulkPanelShell>
    );
};

export const BulkSetCarrierPanel = ({ icon, ...props }) => {
    const { selected } = props;
    const { companySettings } = useCompanySettings() || {};
    const carriers = companySettings?.available_dropoff_carriers;
    const carrierOptions = useMemo(() => [RETIRO_EN_TIENDA, ...getDropoffCarrierOptions(carriers)], [carriers]);
    const [carrier, setCarrier] = useState(null);
    const [address, setAddress] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const { apply, saving, failed } = useBulkApply({
        action: 'set_carrier', selected, onDone: props.onDone,
        successText: (n) => `${pedidos(n)} con ${carrier?.label}`,
    });

    // Direccion por defecto del carrier, la que la compañia configura en Editar compañia. Igual
    // que en Enviar a preparacion: no pisa una escrita a mano.
    const defaultAddressOf = (c) => (c ? getDropoffCarrierByKey(c.value, carriers)?.defaultAddress?.text : '') || '';
    const changeCarrier = (next) => {
        if (!address.trim() || address === defaultAddressOf(carrier)) setAddress(defaultAddressOf(next));
        setCarrier(next);
    };
    const isPickup = carrier?.value === RETIRO_EN_TIENDA.value;
    const canApply = selected.length > 0 && carrier && (isPickup || address.trim().length >= 3);

    // Pedidos a los que el carrier elegido les cambia el que ya traian.
    const overwritten = carrier
        ? selected.filter((o) => o.dropoff_carrier && o.dropoff_carrier !== carrier.value)
        : [];

    return (
        <BulkPanelShell
            {...props}
            title="Asignar carrier"
            icon={icon}
            failed={failed}
            footer={(
                <Button size="small" variant="contained" disabled={saving || !canApply} onClick={() => setConfirmOpen(true)}>
                    {`Asignar a ${selected.length}`}
                </Button>
            )}
        >
            <Autocomplete
                size="small"
                options={carrierOptions}
                value={carrier}
                onChange={(e, v) => changeCarrier(v)}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                renderInput={(params) => <TextField {...params} label="Carrier" />}
            />
            {carrier && !isPickup && (
                <AddressAutocomplete
                    label={`Dirección de entrega a ${carrier.label}`}
                    placeholder="Ej: Av. Providencia 1234, Providencia"
                    value={address}
                    onChange={setAddress}
                    helperText={address && address === defaultAddressOf(carrier) ? 'Dirección por defecto del carrier' : undefined}
                />
            )}
            <ConfirmDialog
                open={confirmOpen}
                title={overwritten.length > 0 ? '¿Sobrescribir el carrier?' : 'Asignar carrier'}
                message={<>
                    <strong>{pedidos(selected.length)}</strong>
                    {isPickup ? <> quedarán como <strong>retiro en tienda</strong>.</> : <> quedarán con dropoff en <strong>{carrier?.label}</strong>:<br />{address}</>}
                    {overwritten.length > 0 && (
                        <Alert severity="warning" sx={{ mt: 1.5 }}>
                            {overwritten.length === 1 ? 'Este pedido ya tenía' : 'Estos pedidos ya tenían'} otro carrier:
                            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2, maxHeight: 160, overflowY: 'auto' }}>
                                {overwritten.map((o) => (
                                    <li key={o.id}>#{o.code}: {getDropoffCarrierDisplay(o.dropoff_carrier, carriers)?.label || o.dropoff_carrier} → <strong>{carrier?.label}</strong></li>
                                ))}
                            </Box>
                        </Alert>
                    )}
                </>}
                confirmText={overwritten.length > 0 ? 'Sobrescribir' : 'Asignar'}
                loading={saving}
                onConfirm={() => apply({ carrier: carrier.value, address: isPickup ? '' : address }).then(() => setConfirmOpen(false))}
                onClose={() => setConfirmOpen(false)}
            />
        </BulkPanelShell>
    );
};

// Cancelar y Eliminar: lo mismo con distinto texto. Eliminar es lo que hace la X de cada fila.
const REMOVE_TEXTS = {
    cancel: {
        title: 'Cancelar pedidos',
        hint: 'El pedido queda Cancelado: no se despacha y sus entregas abiertas se cancelan.',
        button: 'Cancelar',
        done: (n) => `${pedidos(n)} cancelados`,
    },
    delete: {
        title: 'Eliminar pedidos',
        hint: 'El pedido queda Eliminado, igual que con la X de cada fila, y sus entregas abiertas se cancelan.',
        button: 'Eliminar',
        done: (n) => `${pedidos(n)} eliminados`,
    },
};

export const BulkRemovePanel = ({ action, icon, ...props }) => {
    const { selected } = props;
    const texts = REMOVE_TEXTS[action];
    const [confirmOpen, setConfirmOpen] = useState(false);
    const { apply, saving, failed } = useBulkApply({ action, selected, onDone: props.onDone, successText: texts.done });

    return (
        <BulkPanelShell
            {...props}
            title={texts.title}
            icon={icon}
            failed={failed}
            footer={(
                <Button size="small" variant="contained" color="error" disabled={saving || selected.length === 0}
                    onClick={() => setConfirmOpen(true)}>
                    {`${texts.button} ${selected.length}`}
                </Button>
            )}
        >
            <Alert severity="warning">{texts.hint} No se puede deshacer desde esta pantalla.</Alert>
            <ConfirmDialog
                open={confirmOpen}
                title={texts.title}
                message={<>¿Seguro que quieres {texts.button.toLowerCase()} <strong>{pedidos(selected.length)}</strong>?</>}
                confirmText={texts.button}
                confirmColor="error"
                loading={saving}
                onConfirm={() => apply().then(() => setConfirmOpen(false))}
                onClose={() => setConfirmOpen(false)}
            />
        </BulkPanelShell>
    );
};

/**
 * Imprimir etiquetas: separa lo marcado por tipo y genera un PDF por cada uno, con los mismos
 * servicios que los carritos de cada fila. La etiqueta Haulers no entra: se arma pedido por
 * pedido (RUT, forma de pago) y sigue en el icono de la fila.
 *   selected: [{ id, code, label_kind: 'ml' | 'bx' }]
 */
export const BulkPrintLabelsPanel = ({ icon, ...props }) => {
    const { selected } = props;
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(null);
    const ml = selected.filter((o) => o.label_kind === 'ml');
    const bx = selected.filter((o) => o.label_kind === 'bx');

    const print = async (kind, list, fetcher, name) => {
        setLoading(kind);
        try {
            const blob = await fetcher(list.map((o) => o.id));
            const printWindow = window.open(URL.createObjectURL(blob));
            if (printWindow) printWindow.onload = () => { printWindow.print(); };
            enqueueSnackbar(`Etiquetas ${name} generadas`, { variant: 'success' });
            props.onDone({ ok: list.length, failed: [], printedIds: list.map((o) => o.id) });
        } catch (err) {
            let message = null;
            try { message = JSON.parse(await err?.response?.data?.text?.())?.error; } catch (_) { /* no es JSON */ }
            enqueueSnackbar(message || `Error al obtener etiquetas ${name}`, { variant: 'error' });
        }
        setLoading(null);
    };

    return (
        <BulkPanelShell
            {...props}
            title="Imprimir etiquetas"
            icon={icon}
            footer={(
                <>
                    <Button size="small" variant="contained" disabled={!!loading || ml.length === 0}
                        onClick={() => print('ml', ml, orderServices.fetchShipmentLabels, 'Mercado Libre')}>
                        {loading === 'ml' ? 'Generando…' : `Mercado Libre (${ml.length})`}
                    </Button>
                    <Button size="small" variant="contained" disabled={!!loading || bx.length === 0}
                        onClick={() => print('bx', bx, orderServices.fetchBluexpressLabels, 'BlueExpress')}>
                        {loading === 'bx' ? 'Generando…' : `BlueExpress (${bx.length})`}
                    </Button>
                </>
            )}
        >
            <Typography variant="body2" color="text.secondary">
                Sale un PDF por cada tipo. La etiqueta Haulers se imprime desde el ícono de cada pedido.
            </Typography>
        </BulkPanelShell>
    );
};
